import { useEffect, useMemo, useState } from 'react';

import { Avatar, Empty, Field, Ring, StatusBadge } from '../../components/ui/bits.jsx';
import { ExamCard } from '../../components/ExamCard.jsx';
import { useUI } from '../../components/ui/feedback.jsx';
import { DOCS } from '../../lib/exercises.js';
import { useSola } from '../../sola.jsx';
import {
  examCheck,
  getSchoolDays,
  numberFlights,
  subConfirmed,
  subPending,
  subRejected,
  totalSubConfirmed,
  totalSubPending,
  totalSubs,
} from '../../lib/progress.js';
import { fmtDate, fullName, today, vaje, leti as letiWord } from '../../lib/format.js';
import * as api from '../../lib/api.js';
import { Icon } from '../../components/ui/Icon.jsx';
import { VIRI } from '../../lib/viri.js';
import { MODULES } from '../../interactive/modules.js';
import { KVIZ } from '../../interactive/kviz.js';
import { t } from '../../i18n/index.js';

export function StudentDetail({
  user,
  progress,
  flights,
  attendance,
  exams,
  ucenje = [],
  pending,
  bulkBusy,
  onConfirmAll,
  onBack,
  onArchived,
  onPreview,
  onChanged,
  onDeleted,
}) {
  const { toast, confirm } = useUI();
  const { program, pogoji } = useSola();
  const [docs, setDocs] = useState(null); // null = se še nalaga
  const [tab, setTab] = useState('program');

  // Dokumenti so v zasebnem bucketu in zahtevajo podpisane povezave —
  // beremo jih šele, ko je tečajnik izbran, ne za cel seznam.
  useEffect(() => {
    let cancelled = false;
    setDocs(null);
    api.loadDocMap(user.id).then((m) => {
      if (!cancelled) setDocs(m);
    });
    return () => {
      cancelled = true;
    };
  }, [user.id]);

  const check = useMemo(
    () => examCheck(user, progress, flights, docs, exams, { program, pogoji }),
    [user, progress, flights, docs, exams, program, pogoji],
  );

  const run = async (fn, okMsg) => {
    try {
      await fn();
      if (okMsg) toast.ok(okMsg);
      await onChanged();
    } catch (e) {
      toast.bad(e.message ?? t('Shranjevanje ni uspelo.'));
    }
  };

  const remove = async () => {
    const ok = await confirm({
      title: t('Trajno izbrišem {name}?', { name: fullName(user) ?? t('tečajnika') }),
      body: t('Izbrisani bodo vsi podatki. Tega ni mogoče razveljaviti.'),
      detail: [
        t('osebni podatki'),
        t('ves napredek po vajah'),
        t('knjižica letenja'),
        t('dnevnik prisotnosti'),
        t('komentarji inštruktorja'),
      ],
      confirmLabel: t('Trajno izbriši'),
      danger: true,
      typeToConfirm: t('IZBRIŠI'),
    });
    if (!ok) return;
    try {
      await api.deleteStudent(user.id);
      toast.ok(t('Tečajnik je trajno izbrisan.'));
      await onDeleted();
    } catch (e) {
      toast.bad(e.message ?? t('Brisanje ni uspelo.'));
    }
  };

  return (
    <div className="stack">
      <div className="row row--wrap">
        <button className="btn btn--ghost btn--sm admin-back" onClick={onBack}>
          <Icon name="left" size={16} /> {t('Nazaj na seznam')}
        </button>
        <span className="spacer" />
        <button
          className="btn btn--sm btn--secondary"
          onClick={onPreview}
          title={t('Poglej aplikacijo, kot jo vidi tečajnik (samo za gledanje)')}
        >
          <Icon name="users" size={15} /> {t('Poglej kot tečajnik')}
        </button>
      </div>

      <Header
        user={user}
        progress={progress}
        run={run}
        onDelete={remove}
        onArchived={onArchived}
      />

      {pending > 0 && (
        <div className="card card--pad row row--wrap" style={{ background: 'var(--wait-wash)', borderColor: 'var(--wait-border)' }}>
          <span className="spacer t-md t-wait t-bold">
            {t('Čaka potrditev:')} {pending}
          </span>
          <button className="btn btn--sm btn--ok" onClick={onConfirmAll} disabled={bulkBusy}>
            {bulkBusy ? t('Potrjujem …') : t('Potrdi vse')}
          </button>
        </div>
      )}

      <ExamCard
        check={check}
        compact
        onToggleTeorija={(v) =>
          run(() => api.adminToggleTeorija(user.id, v), v ? t('Teorija označena.') : t('Teorija razveljavljena.'))
        }
        onSetZdravniski={(d) =>
          run(
            () => api.updateProfileFields(user.id, { zdravniski_datum: d }),
            d ? t('Datum pregleda je shranjen.') : t('Datum pregleda je izbrisan.'),
          )
        }
      />

      <PersonalInfo user={user} />
      <Notes user={user} run={run} />

      <nav className="tabs" role="tablist" aria-label={t('Podrobnosti tečajnika')}>
        {[
          ['program', t('Program')],
          ['knjizica', `${t('Knjižica')} (${flights.filter((f) => f.status === 'confirmed').length})`],
          ['dnevnik', t('Dnevnik')],
          ['dokumenti', `${t('Dokumenti')} (${docs ? Object.keys(docs).length : '…'}/${DOCS.length})`],
          ['ucenje', `${t('Učenje')} (${new Set(ucenje.map((r) => r.modul)).size}/${MODULES.length})`],
        ].map(([v, label]) => (
          <button key={v} className="tab" role="tab" aria-selected={tab === v} onClick={() => setTab(v)}>
            {label}
          </button>
        ))}
      </nav>

      {tab === 'program' && <ProgramReview user={user} progress={progress} run={run} />}
      {tab === 'knjizica' && <FlightsReview user={user} flights={flights} run={run} />}
      {tab === 'dnevnik' && (
        <Diary user={user} progress={progress} flights={flights} attendance={attendance} run={run} />
      )}
      {tab === 'dokumenti' && <DocsReview docs={docs} />}
      {tab === 'ucenje' && <UcenjeReview ucenje={ucenje} />}
    </div>
  );
}

/* ── Glava ──────────────────────────────────────────────────── */

function Header({ user, progress, run, onDelete, onArchived }) {
  const { confirm } = useUI();
  const { program } = useSola();
  const ALL_EX = program.all;
  const TOTAL = totalSubs(ALL_EX);
  const [editName, setEditName] = useState(false);
  const [editNum, setEditNum] = useState(false);
  const [nameForm, setNameForm] = useState({ ime: user.ime ?? '', priimek: user.priimek ?? '' });
  const [numForm, setNumForm] = useState(user.stevilka != null ? String(user.stevilka) : '');
  const confirmed = totalSubConfirmed(ALL_EX, progress);

  const saveName = () => {
    if (!nameForm.ime.trim()) return;
    run(
      () => api.updateProfileFields(user.id, { ime: nameForm.ime.trim(), priimek: nameForm.priimek.trim() }),
      t('Ime je shranjeno.'),
    ).then(() => setEditName(false));
  };

  const saveNum = () => {
    const n = parseInt(numForm, 10);
    if (!Number.isInteger(n) || n < 1) return;
    run(() => api.updateProfileFields(user.id, { stevilka: n }), t('Številka je shranjena.')).then(() =>
      setEditNum(false),
    );
  };

  /* Po arhiviranju se vrnemo na seznam: tečajnik je zapustil pogled, v
     katerem smo. Na telefonu je detajl čez cel zaslon in brez tega ni poti
     nazaj do zavihka Arhiv. */
  const ask = async (opts, fn, msg, after) => {
    if (!(await confirm(opts))) return;
    await run(fn, msg);
    after?.();
  };

  return (
    <section className="card card--brand card--pad">
      <div className="row row--wrap" style={{ gap: 'var(--s-4)' }}>
        <Avatar ime={user.ime} priimek={user.priimek} size={52} />

        <div className="spacer stack stack--tight">
          {editName ? (
            <div className="row row--wrap">
              <input
                className="input"
                style={{ width: 120 }}
                value={nameForm.ime}
                placeholder={t('Ime')}
                aria-label={t('Ime')}
                onChange={(e) => setNameForm((f) => ({ ...f, ime: e.target.value }))}
              />
              <input
                className="input"
                style={{ width: 140 }}
                value={nameForm.priimek}
                placeholder={t('Priimek')}
                aria-label={t('Priimek')}
                onChange={(e) => setNameForm((f) => ({ ...f, priimek: e.target.value }))}
              />
              <button className="btn btn--sm btn--ok" onClick={saveName}>
                {t('Shrani')}
              </button>
              <button className="btn btn--sm btn--ghost" onClick={() => setEditName(false)}>
                {t('Prekliči')}
              </button>
            </div>
          ) : (
            <div className="row row--wrap" style={{ gap: 'var(--s-2)' }}>
              <h2 className="t-xl">{fullName(user) ?? '—'}</h2>
              <button
                className="btn-icon btn-icon--sm"
                onClick={() => setEditName(true)}
                aria-label={t('Uredi ime')}
              >
                <Icon name="edit" size={15} />
              </button>
              <span className={`badge ${user.placano ? 'badge--ok' : 'badge--bad'}`}>
                <Icon name={user.placano ? 'check' : 'euro'} size={13} />
                {user.placano ? t('Plačano') : t('Neplačano')}
              </span>
            </div>
          )}

          <a className="t-sm t-dim truncate" href={`mailto:${user.email}`}>
            {user.email}
          </a>
          {user.telefon && (
            <a className="row t-sm t-dim" href={`tel:${user.telefon.replace(/\s/g, '')}`} style={{ gap: 'var(--s-2)' }}>
              <Icon name="phone" size={14} /> {user.telefon}
            </a>
          )}

          <div className="row row--wrap" style={{ gap: 'var(--s-2)', marginTop: 'var(--s-1)' }}>
            {editNum ? (
              <>
                <input
                  className="input"
                  type="number"
                  min="1"
                  style={{ width: 90 }}
                  value={numForm}
                  autoFocus
                  aria-label={t('Številka tečajnika')}
                  onChange={(e) => setNumForm(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') saveNum();
                    if (e.key === 'Escape') setEditNum(false);
                  }}
                />
                <button className="btn btn--sm btn--ok" onClick={saveNum} aria-label={t('Shrani številko')}>
                  <Icon name="check" size={15} />
                </button>
                <button className="btn btn--sm btn--ghost" onClick={() => setEditNum(false)} aria-label={t('Prekliči')}>
                  <Icon name="x" size={15} />
                </button>
              </>
            ) : user.testni ? (
              <span className="badge badge--mute">
                <Icon name="flask" size={13} /> {t('Testni račun')}
              </span>
            ) : (
              <>
                <span className="t-sm t-dim">{t('Št. tečajnika')}</span>
                <strong className="t-xl num">{user.stevilka ?? '—'}</strong>
                <button
                  className="btn-icon btn-icon--sm"
                  onClick={() => setEditNum(true)}
                  aria-label={t('Spremeni številko')}
                >
                  <Icon name="edit" size={15} />
                </button>
              </>
            )}
          </div>
        </div>

        <Ring done={confirmed} total={TOTAL} size={78} caption={t('potrjeno')} />
      </div>

      {/* Podatki, ki jih vpiše inštruktor: v svoji vrsti, ne stisnjeni ob ime. */}
      <div className="grid-2 hdr-fields" style={{ marginTop: 'var(--s-3)' }}>
        <label className="field">
          <span className="field__label">{t('Pričetek tečaja')}{!user.datum_tecaja && <span className="badge badge--wait" style={{ marginLeft: 'var(--s-2)' }}>{t('manjka')}</span>}</span>
          <input
            type="date"
            className="input"
            value={user.datum_tecaja ?? ''}
            aria-label={t('Datum pričetka tečaja')}
            title={t('Po tem datumu so razvrščene številke tečajnikov')}
            onChange={(e) =>
              run(
                () => api.updateProfileFields(user.id, { datum_tecaja: e.target.value || null }),
                t('Datum tečaja je shranjen.'),
              )
            }
          />
        </label>
        {/* Tečajniki, vpisani pred tem vprašanjem, ga niso nikoli dobili.
            Vprašaj jih na terenu in vpiši tu — sicer je pregled „Kje nas
            najdejo" prvo sezono prazen in zato neuporaben. */}
        <label className="field">
          <span className="field__label">{t('Kje nas je našel')}</span>
          <select
            className="input"
            value={user.vir_prihoda ?? ''}
            aria-label={t('Kje nas je našel')}
            onChange={(e) =>
              run(
                () => api.updateProfileFields(user.id, { vir_prihoda: e.target.value || null }),
                t('Shranjeno.'),
              )
            }
          >
            <option value="">{t('Ni podatka')}</option>
            {VIRI.map((v) => (
              <option key={v.key} value={v.key}>
                {t(v.label)}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="row row--wrap" style={{ marginTop: 'var(--s-4)', gap: 'var(--s-2)' }}>
        <button
          className={`btn btn--sm ${user.placano ? 'btn--ghost' : 'btn--ok'}`}
          onClick={() =>
            run(
              () =>
                api.updateProfileFields(user.id, {
                  placano: !user.placano,
                  placano_datum: !user.placano ? today() : null,
                }),
              !user.placano ? t('Označeno kot plačano.') : t('Označeno kot neplačano.'),
            )
          }
        >
          {user.placano ? t('Označi neplačano') : t('Označi plačano')}
        </button>

        <button
          className="btn btn--sm btn--secondary"
          onClick={() =>
            ask(
              {
                title: user.testni ? t('Odznačim testni račun?') : t('Označim kot testni račun?'),
                body: user.testni
                  ? t('Račun dobi naslednjo prosto številko tečajnika.')
                  : t('Račun izgubi številko tečajnika in ni več v izvozu.'),
              },
              () => api.updateProfileFields(user.id, { testni: !user.testni }),
              t('Shranjeno.'),
            )
          }
        >
          {user.testni ? t('Ni testni') : t('Testni račun')}
        </button>

        <span className="spacer" />

        {user.archived ? (
          <>
            <button
              className="btn btn--sm btn--secondary"
              onClick={() =>
                ask(
                  { title: t('Aktiviram {name} nazaj?', { name: fullName(user) }) },
                  () => api.updateProfileFields(user.id, { archived: false }),
                  t('Tečajnik je spet aktiven.'),
                  onArchived,
                )
              }
            >
              {t('Aktiviraj nazaj')}
            </button>
            <button className="btn btn--sm btn--danger" onClick={onDelete}>
              <Icon name="trash" size={15} /> {t('Izbriši trajno')}
            </button>
          </>
        ) : (
          <button
            className="btn btn--sm btn--secondary"
            onClick={() =>
              ask(
                {
                  title: t('Arhiviram {name}?', { name: fullName(user) }),
                  body: t('Tečajnika lahko kadarkoli aktiviraš nazaj.'),
                },
                () => api.updateProfileFields(user.id, { archived: true }),
                t('Tečajnik je arhiviran.'),
                onArchived,
              )
            }
          >
            <Icon name="archive" size={15} /> {t('Arhiviraj')}
          </button>
        )}
      </div>
    </section>
  );
}

/* ── Osebni podatki ─────────────────────────────────────────── */

function PersonalInfo({ user }) {
  const rows = [
    [t('Datum rojstva'), fmtDate(user.datum_rojstva)],
    [t('Kraj rojstva'), user.kraj_rojstva],
    [t('Naslov'), user.naslov],
    [t('Pošta'), user.posta],
    [t('Višina'), user.visina_cm ? `${user.visina_cm} cm` : null],
    [t('Teža'), user.teza_kg ? `${user.teza_kg} kg` : null],
    [t('Kontakt v sili'), user.kontaktna_oseba],
    [t('Zdravstvene omejitve'), user.zdravstvene_omejitve],
    [t('Dodatna vprašanja'), user.dodatna_vprasanja],
  ].filter(([, v]) => v);

  if (!rows.length) return null;

  return (
    <details className="card">
      <summary className="card__head" style={{ cursor: 'pointer' }}>
        <span className="card__title spacer">{t('Osebni podatki')}</span>
        <span className="t-xs t-dimmer">{rows.length}</span>
      </summary>
      <div className="card__body grid-auto">
        {rows.map(([k, v]) => (
          <div key={k}>
            <div className="eyebrow">{k}</div>
            <div className="t-md" style={{ wordBreak: 'break-word' }}>
              {v}
            </div>
          </div>
        ))}
      </div>
    </details>
  );
}

/* ── Opombe ─────────────────────────────────────────────────── */

function Notes({ user, run }) {
  const [text, setText] = useState(user.admin_notes ?? '');
  const [saving, setSaving] = useState(false);
  const dirty = text !== (user.admin_notes ?? '');

  useEffect(() => setText(user.admin_notes ?? ''), [user.id, user.admin_notes]);

  return (
    <section className="card card--pad stack stack--tight">
      <div className="row eyebrow" style={{ gap: 'var(--s-2)' }}>
        <Icon name="note" size={14} /> {t('Opombe inštruktorja — vidiš samo ti')}
      </div>
      <textarea
        className="input"
        rows={3}
        value={text}
        placeholder={t('npr. plačal pol tečaja, potrebuje več prakse pri zavojih …')}
        onChange={(e) => setText(e.target.value)}
      />
      <div className="row">
        <span className="spacer t-xs t-dimmer">{dirty ? t('Ni shranjeno') : t('Shranjeno')}</span>
        <button
          className="btn btn--sm btn--primary"
          disabled={!dirty || saving}
          onClick={async () => {
            setSaving(true);
            await run(() => api.saveAdminNotes(user.id, text), t('Opombe so shranjene.'));
            setSaving(false);
          }}
        >
          {saving ? t('Shranjujem …') : t('Shrani opombe')}
        </button>
      </div>
    </section>
  );
}

/* ── Program ────────────────────────────────────────────────── */

function ProgramReview({ user, progress, run }) {
  const { program } = useSola();
  const waiting = totalSubPending(program.all, progress);
  /*
    Inštruktor pride sem z enim namenom: potrditi, kar čaka. Če kaj čaka,
    privzeto pokažemo samo tiste vaje. Prejšnja verzija je izrisala vseh 15
    vaj s 54 pod-vajami naenkrat — stran je bila dolga več kot pet zaslonov
    in čakajoče vaje so se izgubile v njej.
  */
  const [onlyWaiting, setOnlyWaiting] = useState(waiting > 0);

  return (
    <div className="stack">
      <label className="row t-sm card card--pad" style={{ cursor: 'pointer', padding: 'var(--s-3) var(--s-4)' }}>
        <input
          type="checkbox"
          className="checkbox"
          checked={onlyWaiting}
          onChange={(e) => setOnlyWaiting(e.target.checked)}
        />
        <span className="spacer">{t('Pokaži samo vaje, ki čakajo potrditev')}</span>
        {waiting > 0 ? (
          <span className="badge badge--wait num">{waiting}</span>
        ) : (
          <span className="badge badge--ok">{t('Vse potrjeno')}</span>
        )}
      </label>

      {program.sklopi.map(({ key, label, list }) => {
        const shown = onlyWaiting ? list.filter((ex) => subPending(ex, progress) > 0) : list;
        if (!shown.length) return null;
        return (
          <section className="card" key={key}>
            <div className="card__head">
              <span className="card__title spacer">{label}</span>
              <span className="t-xs t-dimmer num">
                {totalSubConfirmed(list, progress)}/{totalSubs(list)} {t('potrjenih')}
              </span>
            </div>
            {shown.map((ex) => (
              <ExerciseReview key={ex.id} ex={ex} user={user} progress={progress} run={run} />
            ))}
          </section>
        );
      })}
    </div>
  );
}

function ExerciseReview({ ex, user, progress, run }) {
  const ep = progress[ex.id] ?? {};
  const waiting = subPending(ex, progress);
  const confirmed = subConfirmed(ex, progress);
  const rejected = subRejected(ex, progress);
  const [open, setOpen] = useState(waiting > 0);
  const [comment, setComment] = useState(ep.instructor_comment ?? '');

  useEffect(() => setComment(ep.instructor_comment ?? ''), [user.id, ex.id, ep.instructor_comment]);

  return (
    <div style={{ borderBottom: '1px solid var(--border)' }}>
      <div className={`row${waiting ? ' list__row--wait' : ''}`} style={{ padding: 'var(--s-2) var(--s-4)' }}>
        <button
          className="row spacer"
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
          style={{ textAlign: 'left', minHeight: 'var(--tap)' }}
        >
          <span className="spacer">
            <span className="t-md t-bold" style={{ display: 'block' }}>
              {ex.title}
            </span>
            <span className="t-xs t-dim num">
              {confirmed}/{ex.subs.length} {t('potrjenih')}
              {waiting > 0 && <span className="t-wait"> · {waiting} {t('čaka')}</span>}
              {rejected > 0 && <span className="t-bad"> · {rejected} {t('zavrnjenih')}</span>}
            </span>
          </span>
          <Icon name={open ? 'up' : 'down'} size={18} className="t-dimmer" />
        </button>

        {waiting > 0 && (
          <button
            className="btn btn--sm btn--ok"
            onClick={() => run(() => api.adminConfirmExercise(user.id, ex.id), t('Vaja potrjena.'))}
          >
            {t('Potrdi vajo')} ({waiting})
          </button>
        )}
      </div>

      {open && (
        <>
          {ex.subs.map((sub, si) => {
            const sd = ep.subs?.[si] ?? {};
            const waitingSub = sd.status === 'pending' || (!sd.status && sd.done);
            return (
              <div
                key={si}
                className={`row row--wrap${sd.status === 'pending' ? ' list__row--wait' : sd.status === 'confirmed' ? ' list__row--ok' : ''}`}
                style={{ padding: 'var(--s-2) var(--s-4) var(--s-2) var(--s-6)', borderTop: '1px solid var(--border)' }}
              >
                {/* Besedilo pod-vaje čez celo širino na telefonu; datum, značka in gumbi v drugo vrstico. */}
                <span className="t-md" style={{ flex: '1 1 200px', minWidth: 0 }}>{sub}</span>
                {sd.date && <span className="t-xs t-dimmer num">{fmtDate(sd.date)}</span>}
                <StatusBadge status={sd.status ?? (sd.done ? 'pending' : null)} />

                {waitingSub && (
                  <span className="row" style={{ gap: 'var(--s-1)' }}>
                    <button
                      className="btn btn--sm btn--ok"
                      aria-label={t('Potrdi: {sub}', { sub })}
                      onClick={() => run(() => api.adminConfirmSub(user.id, ex.id, si, 'confirmed'))}
                    >
                      <Icon name="check" size={15} />
                    </button>
                    <button
                      className="btn btn--sm btn--danger"
                      aria-label={t('Zavrni: {sub}', { sub })}
                      onClick={() => run(() => api.adminConfirmSub(user.id, ex.id, si, 'rejected'))}
                    >
                      <Icon name="x" size={15} />
                    </button>
                  </span>
                )}

                {sd.status === 'confirmed' && (
                  <button
                    className="btn btn--sm btn--ghost"
                    onClick={() => run(() => api.adminConfirmSub(user.id, ex.id, si, 'pending'))}
                  >
                    {t('Razveljavi')}
                  </button>
                )}
              </div>
            );
          })}

          <div style={{ padding: 'var(--s-3) var(--s-4)' }}>
            {ep.comment && (
              <p className="row t-sm t-dim" style={{ marginBottom: 'var(--s-2)', gap: 'var(--s-2)', alignItems: 'flex-start' }}>
                <Icon name="note" size={14} /> {t('Tečajnik')}: „{ep.comment}“
              </p>
            )}
            <div className="row">
              <input
                className="input"
                value={comment}
                placeholder={t('Komentar inštruktorja …')}
                aria-label={t('Komentar za {ex}', { ex: ex.title })}
                onChange={(e) => setComment(e.target.value)}
              />
              <button
                className="btn btn--sm btn--primary"
                disabled={comment === (ep.instructor_comment ?? '')}
                onClick={() => run(() => api.adminSaveComment(user.id, ex.id, comment), t('Komentar shranjen.'))}
              >
                {t('Shrani')}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/* ── Knjižica ───────────────────────────────────────────────── */

function FlightsReview({ user, flights, run }) {
  const { pogoji, sola } = useSola();
  const { toast } = useUI();
  const rows = useMemo(() => numberFlights(flights), [flights]);

  return (
    <section className="card">
      <div className="card__head">
        <span className="card__title spacer">
          {t('Knjižica letenja')} — {flights.filter((f) => f.status === 'confirmed').length} {t('potrjenih')}
        </span>
        <button
          className="btn btn--sm btn--secondary"
          disabled={!flights.length}
          onClick={async () => {
            try {
              const { exportFlightLogPDF } = await import('../../lib/pdf.js');
              await exportFlightLogPDF(user, flights, { minLetov: pogoji.min_letov, sola });
            } catch (e) {
              toast.bad(t('PDF ni bilo mogoče ustvariti.') + ' ' + (e.message ?? ''));
            }
          }}
        >
          <Icon name="download" size={15} /> PDF
        </button>
      </div>

      {rows.length === 0 ? (
        <Empty icon="plane" title={t('Ni vpisanih letov')} />
      ) : (
        <div className="list">
          {rows.map((f) => (
            <div key={f.id} className={`list__row${f.status === 'pending' ? ' list__row--wait' : ''}`}>
              <span className="num t-sm t-dimmer" style={{ minWidth: 28 }}>
                {f.seq}
              </span>
              <div className="spacer">
                <div className="t-md t-bold">{fmtDate(f.flight_date)}</div>
                <div className="t-sm t-dim truncate">
                  {f.vzletisce}
                  {f.opomba ? ` · ${f.opomba}` : ''}
                </div>
              </div>
              <StatusBadge status={f.status} />
              {f.status === 'pending' && (
                <span className="row" style={{ gap: 'var(--s-1)' }}>
                  <button
                    className="btn btn--sm btn--ok"
                    aria-label={t('Potrdi let')}
                    onClick={() => run(() => api.adminConfirmFlight(f.id, 'confirmed'))}
                  >
                    <Icon name="check" size={15} />
                  </button>
                  <button
                    className="btn btn--sm btn--danger"
                    aria-label={t('Zavrni let')}
                    onClick={() => run(() => api.adminConfirmFlight(f.id, 'rejected'))}
                  >
                    <Icon name="x" size={15} />
                  </button>
                </span>
              )}
              {f.status === 'confirmed' && (
                <button
                  className="btn btn--sm btn--ghost"
                  onClick={() => run(() => api.adminConfirmFlight(f.id, 'pending'))}
                >
                  {t('Razveljavi')}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

/* ── Dnevnik ────────────────────────────────────────────────── */

function Diary({ user, progress, flights, attendance, run }) {
  const { program } = useSola();
  const { confirm } = useUI();
  const [form, setForm] = useState({ attendance_date: '', note: '' });
  const days = useMemo(
    () => getSchoolDays(progress, flights, attendance, program),
    [progress, flights, attendance, program],
  );

  return (
    <section className="card">
      <div className="card__head">
        <span className="card__title">{t('Dnevnik prisotnosti')} — {days.length}</span>
      </div>

      <div className="card__body row row--wrap" style={{ alignItems: 'flex-end' }}>
        <Field label={t('Datum')} id="ad-date">
          <input
            id="ad-date"
            type="date"
            className="input"
            value={form.attendance_date}
            onChange={(e) => setForm((f) => ({ ...f, attendance_date: e.target.value }))}
          />
        </Field>
        <div className="spacer">
          <Field label={t('Opomba')} id="ad-note">
            <input
              id="ad-note"
              className="input"
              placeholder={t('npr. teorija, opazovanje')}
              value={form.note}
              onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
            />
          </Field>
        </div>
        <button
          className="btn btn--primary"
          disabled={!form.attendance_date}
          onClick={async () => {
            await run(() => api.addAttendance(user.id, form), t('Dan je dodan.'));
            setForm({ attendance_date: '', note: '' });
          }}
        >
          {t('+ Dodaj dan')}
        </button>
      </div>

      {days.length === 0 ? (
        <Empty icon="calendar" title={t('Še ni zabeleženih dni')} />
      ) : (
        <div className="list">
          {days.map((day) => (
            <div key={day.date} className="list__row" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
              <div className="row row--wrap">
                <strong className="t-md t-brand">{fmtDate(day.date)}</strong>
                {day.exercises.length > 0 && (
                  <span className="badge badge--brand num">
                    {day.exercises.length} {vaje(day.exercises.length)}
                  </span>
                )}
                {day.flights > 0 && (
                  <span className="badge badge--ok num">
                    {day.flights} {letiWord(day.flights)}
                  </span>
                )}
                {day.manual && <span className="badge badge--mute">{t('Ročno')}</span>}
                <span className="spacer" />
                {day.manual && (
                  <button
                    className="btn-icon btn-icon--sm"
                    aria-label={t('Izbriši dan {d}', { d: fmtDate(day.date) })}
                    onClick={async () => {
                      if (await confirm({ title: t('Izbrišem ta dan?'), danger: true, confirmLabel: t('Izbriši') })) {
                        run(() => api.deleteAttendance(day.manualId), t('Dan je izbrisan.'));
                      }
                    }}
                  >
                    <Icon name="x" size={14} />
                  </button>
                )}
              </div>
              {day.note && (
                <p className="row t-sm t-dim" style={{ gap: 'var(--s-2)' }}>
                  <Icon name="note" size={14} /> {day.note}
                </p>
              )}
              {day.exercises.length > 0 && (
                <ul className="t-sm t-dim" style={{ listStyle: 'none' }}>
                  {day.exercises.map((e, i) => (
                    <li key={i}>
                      <Icon
                        name={e.status === 'confirmed' ? 'check' : 'clock'}
                        size={13}
                        className={e.status === 'confirmed' ? 't-ok' : 't-wait'}
                        style={{ display: 'inline-block', verticalAlign: '-2px' }}
                      />{' '}
                      {e.subTitle}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

/* ── Dokumenti ──────────────────────────────────────────────── */

function DocsReview({ docs }) {
  return (
    <section className="card">
      <div className="card__head">
        <span className="card__title">{t('Dokumenti')}</span>
      </div>
      <div className="list">
        {DOCS.map((doc) => {
          const d = docs?.[doc.id];
          return (
            <div key={doc.id} className="list__row">
              <Icon name={doc.id === 'zdravniski' ? 'file' : 'idCard'} size={20} className="t-dim" />
              <div className="spacer">
                <div className="t-md t-bold">{t(doc.label)}</div>
                {docs === null ? (
                  <div className="t-sm t-dimmer">{t('Nalagam …')}</div>
                ) : d ? (
                  <div className="row t-sm t-ok" style={{ gap: 'var(--s-1)' }}>
                    <Icon name="check" size={13} /> {t('Naloženo')}{d.uploadedAt ? ` · ${d.uploadedAt}` : ''}
                  </div>
                ) : (
                  <div className="t-sm t-dimmer">{t('Ni naloženo')}</div>
                )}
              </div>
              {d && (
                <a className="btn btn--sm btn--secondary" href={d.url} target="_blank" rel="noopener noreferrer">
                  {t('Oglej')}
                </a>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

/* ── Interaktivno učenje ────────────────────────────────────
   Kaj je tečajnik predelal v zavihku Učenje: opravljene stopnje simulacij
   in rezultat kratkega preverjanja. Samo zapisi iz baze — kar je tečajnik
   delal brez povezave, se pokaže, ko se zapiše. */

function UcenjeReview({ ucenje }) {
  return (
    <section className="card">
      <div className="card__head">
        <span className="card__title">{t('Interaktivno učenje')}</span>
      </div>
      <div className="list">
        {MODULES.map((m) => {
          const rows = ucenje.filter((r) => r.modul === m.id);
          const levels = (m.levels ?? []).filter((k) => rows.some((r) => r.kljuc === k));
          const kv = rows.find((r) => r.kljuc === 'kviz');
          const last = rows.map((r) => r.updated_at).filter(Boolean).sort().pop();
          const full = kv && kv.pravilnih === kv.vseh;
          return (
            <div key={m.id} className="list__row">
              <Icon name={m.icon} size={20} className={rows.length ? 't-brand' : 't-dimmer'} />
              <div className="spacer">
                <div className="t-md t-bold">{m.title}</div>
                <div className="t-sm t-dim">
                  {m.levels?.length
                    ? t('Stopnje: {a} od {b}', { a: levels.length, b: m.levels.length })
                    : null}
                  {KVIZ[m.id] ? ` · ${t('Preverjanje:')} ${kv ? `${kv.pravilnih}/${kv.vseh}` : t('ni rešeno')}` : ''}
                  {last ? ` · ${fmtDate(last.slice(0, 10))}` : ''}
                </div>
              </div>
              {levels.length === (m.levels?.length ?? 0) && full ? (
                <span className="row t-sm t-ok" style={{ gap: 'var(--s-1)' }}>
                  <Icon name="check" size={14} /> {t('Predelano')}
                </span>
              ) : rows.length ? (
                <span className="t-sm t-wait">{t('Začeto')}</span>
              ) : (
                <span className="t-sm t-dimmer">{t('Ni začeto')}</span>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
